/**
 * MapControls - Presenter Component
 *
 * Pure presenter that renders the map control toolbar buttons.
 * Receives all data and action callbacks from MapControlsContainer
 * (or directly from MapPanelContainer during the migration transition).
 * Contains ZERO store access.
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { Button, Space, Tooltip, Divider, Select } from 'antd';
import {
  BorderOutlined,
  ExpandOutlined,
  GatewayOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FlagOutlined,
  LineOutlined,
  BlockOutlined,
  RadiusUpleftOutlined,
  FormatPainterOutlined,
  AppstoreAddOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  BranchesOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import type { Image, InteractionState, GridSettings, StoreState } from '../../types/types';
import { useGridSettingsModal } from '../../hooks/useModal';

/**
 * Props that are passed through from the parent (MapPanelContainer)
 * and not sourced from the store by MapControlsContainer.
 */
export interface MapControlsPassthroughProps {
  onResetView: () => void;
  onFitImage: () => void;
  drawingMode: string | null;
  setDrawingMode: (mode: string | null) => void;
  maps: Image[];
  activeMapId: number | null;
  onMapChange: (mapId: number | null) => void;
  isMapActive: boolean;
  interaction: InteractionState | null;
  gridSettings: GridSettings;
  saveRegion: () => void;
}

/**
 * Full props interface including settings that come from the Container.
 */
export interface MapControlsProps extends MapControlsPassthroughProps {
  fogSettings: StoreState['settings']['fogOfWar'];
  showMapRegions: boolean;
  showMapMarkers: boolean;
  onUpdateSettings: (settings: Partial<StoreState['settings']>) => void;
}

const MapControls: React.FC<MapControlsProps> = ({
  onResetView,
  onFitImage,
  drawingMode,
  setDrawingMode,
  maps,
  activeMapId,
  onMapChange,
  isMapActive,
  interaction,
  gridSettings,
  saveRegion,
  fogSettings,
  showMapRegions,
  showMapMarkers,
  onUpdateSettings,
}) => {
  const { show: showGridSettingsModal } = useGridSettingsModal();

  const handleDrawingMode = (mode: string) => {
    // If we are turning OFF 'region-click' mode and there's a selection, save it.
    if (drawingMode === 'region-click' && mode === 'region-click' && interaction?.type === 'region' && interaction.selectedCells.size > 0) {
      saveRegion();
      return; // The saveRegion flow will handle resetting the mode.
    }
    setDrawingMode(drawingMode === mode ? null : mode);
  };

  return (
    <div>
      <Space wrap>
        <Select
          showSearch
          value={activeMapId}
          onChange={onMapChange}
          style={{ width: 150 }}
        >
          {(maps ?? []).map(map => <Select.Option key={map.id} value={map.id}>{map.name}</Select.Option>)}
        </Select>
        <Tooltip title="Reset View (Zoom & Pan)" placement="left">
          <Button icon={<ExpandOutlined />} onClick={onResetView} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Fit Image to Screen" placement="left">
          <Button icon={<BorderOutlined />} onClick={onFitImage} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Grid Settings" placement="left">
          <Button icon={<GatewayOutlined />} onClick={showGridSettingsModal} disabled={!isMapActive} />
        </Tooltip>
        <Divider style={{ margin: '4px 0' }} />
        <Tooltip title="FoW" placement="left">
          <Button
            icon={fogSettings?.enabled ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => onUpdateSettings({ fogOfWar: { ...fogSettings, enabled: !fogSettings?.enabled } })}
            type={fogSettings?.enabled ? 'primary' : 'default'}
            disabled={!isMapActive}
          />
        </Tooltip>
        <Tooltip title="Regions" placement="left">
          <Button
            icon={<EnvironmentOutlined />}
            onClick={() => onUpdateSettings({ showMapRegions: !showMapRegions })}
            type={showMapRegions ? 'primary' : 'default'}
            disabled={!isMapActive}
          />
        </Tooltip>
        <Tooltip title="Markers" placement="left">
          <Button
            icon={<PushpinOutlined />}
            onClick={() => onUpdateSettings({ showMapMarkers: !showMapMarkers })}
            type={showMapMarkers ? 'primary' : 'default'}
            disabled={!isMapActive}
          />
        </Tooltip>
        <Divider style={{ margin: '4px 0' }} />
        <Tooltip title="Draw Journey" placement="left">
          <Button icon={<FlagOutlined />} onClick={() => handleDrawingMode('journey')} type={drawingMode === 'journey' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Draw Line" placement="left">
          <Button icon={<LineOutlined />} onClick={() => handleDrawingMode('line')} type={drawingMode === 'line' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Draw Rectangle" placement="left">
          <Button icon={<BlockOutlined />} onClick={() => handleDrawingMode('rect')} type={drawingMode === 'rect' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Draw Circle" placement="left">
          <Button icon={<RadiusUpleftOutlined />} onClick={() => handleDrawingMode('circle')} type={drawingMode === 'circle' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Draw Cone" placement="left">
          <Button icon={<ThunderboltOutlined />} onClick={() => handleDrawingMode('cone')} type={drawingMode === 'cone' ? 'primary' : 'default'} disabled={!isMapActive || gridSettings?.type !== 'hex'} />
        </Tooltip>
        <Tooltip title="Draw Grid Path" placement="left">
          <Button icon={<BranchesOutlined />} onClick={() => handleDrawingMode('grid-path')} type={drawingMode === 'grid-path' ? 'primary' : 'default'} disabled={!isMapActive || gridSettings?.type !== 'hex'} />
        </Tooltip>
        <Tooltip title="Paint Region" placement="left">
          <Button icon={<FormatPainterOutlined />} onClick={() => handleDrawingMode('region')} type={drawingMode === 'region' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
        <Tooltip title="Add by Click" placement="left">
          <Button icon={<AppstoreAddOutlined />} onClick={() => handleDrawingMode('region-click')} type={drawingMode === 'region-click' ? 'primary' : 'default'} disabled={!isMapActive} />
        </Tooltip>
      </Space>
    </div>
  );
};

export default MapControls;
